import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'

import {
  AbsorptionType,
  AllocationClassification,
  ExplicitCreditReason,
  ManualRefundSource,
  ReviewReason,
} from './reconciliation-v2-enums'
import type { PullDayFilter, ReviewQueueKind } from './reconciliation-v2-enums'

// Enums live in the axios-free reconciliation-v2-enums module so recon-v2-helpers
// and the ReviewQueue helpers can import them without loading this api-client;
// re-exported here for existing import sites.
export {
  AbsorptionType,
  AllocationClassification,
  ExplicitCreditReason,
  ManualRefundSource,
  ReviewReason,
}
export type { ReviewQueueKind }

// ── money wire conversion ────────────────────────────────────────────────────
// The reconciliation-v2 API speaks integer CENTS on the wire (money fields are
// suffixed `*Cents`). Components, helpers, and all split math work in DOLLARS.
// This api-client is the anti-corruption boundary: responses are mapped
// cents→dollars here (so the exported view-model interfaces below stay in
// dollars) and request payloads are converted dollars→cents right before they
// hit the wire. Every money field is converted exactly ONCE — never re-convert
// in a component or helper.

/** cents→dollars. Exact for integer cents. Exported for unit tests. */
export const centsToDollars = (cents: number): number => cents / 100

/**
 * dollars→cents for outbound payloads. `Math.round` guards float drift
 * (e.g. 12.55 * 100 === 1254.9999…) and the sign is preserved so refunds stay
 * negative. Exported for unit tests.
 */
export const dollarsToCents = (dollars: number): number =>
  Math.round(dollars * 100)

export enum PeriodStatus {
  Paid = 'paid',
  Underpaid = 'underpaid',
  Overpaid = 'overpaid',
  Unpaid = 'unpaid',
  UnknownExpected = 'unknown_expected',
}

/**
 * One payment that landed in a benefit-month. Returned in
 * `MonthlyTotal.payments` so the BenefitsTable month cell can render conf #,
 * token, provider pill, and "moved from" without a per-period detail fetch.
 */
export interface MonthlyPayment {
  amount: string
  /** 'marqeta' | 'wex' | 'i_stream' | 'check' | 'group_ach' | 'ee_paid' | null */
  source: string | null
  token: string | null
  /** Card last-4 (Marqeta/WEX only); null for non-card sources. */
  card: string | null
  confirmationCode: string | null
  /** Period (YYYY-MM-DD) the slice was moved away from, when applicable. */
  movedFrom?: string | null
  /** ISO transaction date; the cell shows the latest across its payments. */
  transactionDate?: string | null
}

export interface MonthlyTotal {
  totalPaid: number
  status: PeriodStatus
  /**
   * Materialized auto-issue classification (null = no auto issue). Single source
   * of truth shared with the drawer — drives the cell's anomaly/red state.
   * `status` is kept only for the paid-vs-expected display relationship.
   */
  issueType?: 'overpaid' | 'underpaid' | null
  /**
   * Server-flagged: this period was an unresolved overpaid/underpaid anomaly
   * that an operator has acknowledged. Drives the neutral-bg + "!" overlay on
   * BenefitsTable cells.
   */
  issueResolved?: boolean
  /**
   * Per-period expected amount for this benefit-month (null = not set). This is
   * what the member was INVOICED on — the cell displays it unchanged. Compare
   * paid against `netDue`, not against this.
   */
  expected: number | null
  /** Sum of the explicit credits on this benefit-month; 0 when uncredited. */
  credited: number
  /**
   * `expected` less `credited` — what the member still owes. Null when no
   * expected resolves. The server's `status` / `issueType` follow THIS, so any
   * paid-vs-expected comparison in the UI must follow it too or the grid and
   * the Adjust drawer will disagree.
   */
  netDue: number | null
  /** True when this benefit-month has any history/event activity. */
  hasActivity: boolean
  /**
   * True when this benefit-month has an open (unresolved) MANUAL_REVIEW flag.
   * Independent of paid-vs-expected — makes the cell read as an issue (red bg)
   * even when the numbers reconcile.
   */
  manualFlagged?: boolean
  /**
   * payment_assignment.payment_type recorded for this benefit-month
   * (e.g. 'check' | 'ach' | 'card'). Null when no payment assignment exists.
   */
  paymentType?: string | null
  /** Individual payments that make up `totalPaid` for this benefit-month. */
  payments: MonthlyPayment[]
}

export interface BenefitInsuredPerson {
  firstName: string
  lastName: string
  /** Normalized to lowercase: 'employee' | 'spouse' | 'child' | ... */
  relationship: string
}

export interface BenefitRow {
  benefitId: string
  employeeName: string
  employeePublicKey: string
  insuredPersons: BenefitInsuredPerson[]
  carrierName: string | null
  benefitEffectiveDate: string | null
  benefitEndDate: string | null
  employmentStatus: string | null
  terminationDate: string | null
  clientId: string | null
  companyName: string | null
  monthlyTotals: Record<string, MonthlyTotal>
  carrierExpected: number | null
  surecoExpected: number | null
  /**
   * Latest payment type (payment_assignment.payment_type) for the benefit —
   * from the most recent (year, month) payment assignment. Null when none.
   */
  latestPaymentType: string | null
  /**
   * The benefit's primary payment method, read live like Payment Assignment's
   * column. Absent on servers that predate the field.
   */
  primaryPaymentMethod?: string | null
  /**
   * Payment assignment whose detail feeds the payment method section: this
   * month's, else the most recent. Null when none; absent on older servers.
   */
  paymentAssignmentId?: number | null
  latestTicketUrl: string | null
  ticketCount: number
  latestTxDate: string | null
  latestEditDate: string | null
  /** The benefit's autopay pull day of month (1–31); null = not set. */
  pullDay: number | null
  /**
   * True when the current payment period is on autopay, its pull date + 1 day
   * has passed, and nothing is allocated to it.
   */
  hasMissedPull: boolean
}

export enum BenefitSortKey {
  InsuredPersons = 'insuredPersons',
  EmployeeName = 'employeeName',
  CompanyName = 'companyName',
  CarrierName = 'carrierName',
  BenefitEffectiveDate = 'benefitEffectiveDate',
  LatestTxDate = 'latestTxDate',
  LatestEditDate = 'latestEditDate',
}

export interface ListBenefitsResponse {
  data: BenefitRow[]
  total: number
  page: number
  limit: number
}

export interface PeriodEventView {
  id: string
  eventType: string
  payload: Record<string, unknown>
  createdBy: string | null
  createdAt: string
}

export interface PeriodSuggestion {
  periodStartDate: string
  expectedAmount: number | null
  currentTotal: number
  status: PeriodStatus
  pastBenefitEnd: boolean
  preBenefitStart: boolean
  /** Lower is better — 0 is an exact match. */
  score: number
  reasons: string[]
}

export interface PeriodIssueState {
  type: 'overpaid' | 'underpaid' | null
  resolved: boolean
  resolvedAt: string | null
  resolvedBy: string | null
  resolvedNote: string | null
}

/** One live slice of the mother transaction, across any benefit/period. */
export interface MotherTransactionSlice {
  benefitId: string
  benefitLabel: string
  /**
   * False when the benefit is outside recon-v2's benefits projection
   * (deactivated upstream, or missing from the sync), so `benefitLabel` is the
   * bare id. Treat anything but an explicit `false` as resolved — an older
   * server omits the field.
   */
  benefitResolved: boolean
  employeePublicKey: string | null
  carrierName: string | null
  insuredPersons: BenefitInsuredPerson[]
  /** Period this slice sits in (YYYY-MM-DD). */
  periodStartDate: string
  /** Signed dollar amount of this slice (negative for refund slices). */
  amount: string
  classification: AllocationClassification | null
}

/**
 * One parent transaction allocated to a benefit+period cell — its full signed
 * amount, the portion landing in THIS period, and every live slice across all
 * benefits/periods. The Adjust modal lists one selectable row per entry and
 * re-splits the SELECTED parent from its full amount.
 */
export interface PeriodDetailTransaction {
  transactionId: string
  /** Human-facing transaction number (e.g. M000101); null when unnumbered. */
  transactionNumber: string | null
  /** ISO timestamp of the real transaction; null when the tx row is missing. */
  transactionDate: string | null
  source: string | null
  /** Signed real transaction total (negative for refund/chargeback). */
  fullAmount: number
  /** Sum of this tx's allocation amounts in THIS benefit+period (signed). */
  amountInThisPeriod: number
  /** ALL live slices of the tx across benefits/periods. */
  slices: MotherTransactionSlice[]
}

/**
 * One explicit credit sitting on a period — a reduction the carrier applied to
 * what it charged, recorded as its own line item so `expectedAmount` (the number
 * the member was invoiced on) stays intact.
 */
export interface PeriodDetailCredit {
  id: string
  /** Positive dollar magnitude the carrier did not charge. */
  amount: number
  reason: ExplicitCreditReason
  note: string | null
  /**
   * The operator who granted the credit. Never null — explicit_credit.created_by
   * is NOT NULL and EPS refuses a credit request it cannot attribute.
   */
  createdBy: string
  createdAt: string
  /**
   * True once the invoicing side has claimed the credit — it can no longer be
   * deleted (the server answers 409).
   */
  consumed: boolean
}

export interface PeriodDetail {
  benefitId: string
  periodStartDate: string
  totalPaid: number
  /** What the member was invoiced on; see {@link netDue} for what is still owed. */
  expectedAmount: number | null
  /** Every explicit credit on this period, oldest-first. */
  credits: PeriodDetailCredit[]
  /**
   * `expectedAmount` less the credited total — what the member still owes. Null
   * when no expected resolves. Issue derivation compares paid against THIS.
   */
  netDue: number | null
  status: PeriodStatus
  /** Latest confirmation code seen for this benefit-period, when available. */
  confirmationCode: string | null
  issue: PeriodIssueState
  /**
   * True when this benefit-month has an open MANUAL_REVIEW flag — read from the
   * same materialized source as the grid, so the drawer's flag button and the
   * grid cell share one source of truth.
   */
  manualFlagged?: boolean
  events: PeriodEventView[]
  /**
   * Every distinct transaction allocated to this benefit+period, in allocation
   * order, each with its full cross-benefit/period allocation state. Drives
   * the Adjust modal's per-parent-transaction management of the cell.
   */
  transactions: PeriodDetailTransaction[]
  /** The benefit's autopay pull day of month (1–31); null = not set. */
  pullDay: number | null
  /**
   * True only when this period is the current payment period and its autopay
   * pull is missed; false for any other period.
   */
  isMissedPull: boolean
}

interface ListBenefitsParams {
  search?: string
  carrier?: string
  clientId?: string
  /** true = only benefits with at least one unresolved anomaly; false = none; undefined = no filter */
  hasIssue?: boolean
  sortBy?: BenefitSortKey
  sortOrder?: 'asc' | 'desc'
  /** YYYY-MM-DD — surface only benefits with no allocation for this month */
  emptyInMonth?: string
  /** Restrict results to benefits whose effective/end window overlaps this year. */
  year?: number
  /** Autopay pull days 1–31; `PULL_DAY_NOT_SET` selects benefits with none. */
  pullDays?: PullDayFilter[]
  /** true = only benefits whose current-payment-period autopay pull is missed. */
  missedPull?: boolean
  /**
   * true = only benefits with an explicit credit on a period in `year`; false =
   * none in `year`, and EPS rejects it combined with `creditReasons` or
   * `noteSearch`. A reason or note text alone already implies true.
   */
  hasExplicitCredit?: boolean
  /** One credit must carry any listed reason — the same credit that matches `noteSearch`. */
  creditReasons?: ExplicitCreditReason[]
  /** Free text matched against explicit credit notes only. Separate from `search`. */
  noteSearch?: string
  page?: number
  limit?: number
}

/**
 * A transaction's identity as its source record states it — today only the
 * iStream ACH file. Never verified against our benefits: EPS sends it only on
 * an UNRESOLVED iStream row, and it is display-only. Each field is null when
 * the source left it empty.
 */
export interface ReviewSourceReported {
  benefitId: string | null
  /** The insured's first and last name, joined. */
  insuredName: string | null
  employeeName: string | null
}

export interface ReviewTransaction {
  transactionId: string
  /**
   * The human-facing ledger number — what an operator quotes to finance, and
   * what a colleague can find a payment by. Always present: EPS's queue query
   * excludes any transaction without one, an unnumbered row not yet being a
   * human-actionable item.
   */
  transactionNumber: string
  transactionDate: string
  amount: number
  transactionType: string
  transactionStatus: string
  source: string
  reason: ReviewReason
  benefitId: string | null
  /** The resolved benefit's carrier. Null exactly when `benefitId` is. */
  carrierName: string | null
  employeeName: string | null
  employeePublicKey: string | null
  insuredPersons: BenefitInsuredPerson[]
  /**
   * What the source record says this transaction belongs to. Non-null only on
   * an unresolved iStream row; null on every resolved row, whatever the source
   * says. Its presence is the cue to show those values with a warning.
   */
  sourceReported: ReviewSourceReported | null
  /**
   * The reference each card rail is looked up by — Marqeta's per-transaction
   * token, WEX's merchant log id. Null for every non-card rail (check, Group
   * ACH, iStream, EE paid), which have none at all: the Token column renders a
   * dash there rather than standing the internal transaction id in its place.
   *
   * The two halves are not the same KIND of id — the WEX one identifies the
   * card, so it repeats across that card's rows. That is deliberate: it is the
   * id every `/vcn/wex/:merchantLogId` route takes, and the per-transaction
   * alternative (the TAS event id) can be looked up nowhere.
   */
  token: string | null
  createdAt: string
}

export type ReconIssueType = 'overpaid' | 'underpaid' | 'manual_review'

/** One unresolved overpaid/underpaid benefit-month — a review-queue issue row. */
export interface ReconIssueRow {
  benefitId: string
  /** First day of the affected month (YYYY-MM-DD). */
  periodStartDate: string
  issueType: ReconIssueType
  expected: number | null
  paid: number
  /** paid - expected (signed). */
  discrepancy: number
  employeeName: string | null
  employeePublicKey: string | null
  insuredPersons: {
    firstName: string
    lastName: string
    relationship: string
  }[]
  carrierName: string | null
  companyName: string | null
}

/**
 * Segmented-control / query value for the unified Review queue. 'all' shows
 * both sources; a ReviewReason narrows to transactions; a ReconIssueType
 * narrows to discrepancy issues.
 */
export type ReviewQueueFilter = 'all' | `${ReviewReason}` | ReconIssueType

/**
 * One row of the unified Review queue — a transaction the auto-allocator could
 * not place OR an open discrepancy issue. Exactly one of `transaction` /
 * `issue` is set, discriminated by `kind`. The backend returns them as a single
 * date-sorted, single-paginated feed (sorted by `sortDate`).
 */
export interface ReviewQueueItem {
  kind: 'transaction' | 'issue'
  sortDate: string
  transaction: ReviewTransaction | null
  issue: ReconIssueRow | null
}

/**
 * A key of the per-filter {@link ReviewQueueCounts} map — exactly the filter
 * vocabulary minus 'all', because 'all' is not a bucket the backend counts. It
 * is the SUM of this map's values for the requested `kind`.
 */
export type ReviewQueueCountKey = Exclude<ReviewQueueFilter, 'all'>

/**
 * Row count per filter chip, scoped to the request's `kind` and `search` but
 * deliberately NOT to its `filter` — otherwise every chip would show the count
 * of the chip already selected.
 *
 * Every key is optional because the EPS schema is `.partial()`. Semantically a
 * key belonging to the requested `kind` is always present, at zero if empty, so
 * an absent key means "not part of this kind" — never "zero". Read sites
 * zero-fill for the keys they know belong to their arm (`counts.refund ?? 0`)
 * and render nothing for the rest.
 */
export type ReviewQueueCounts = Partial<Record<ReviewQueueCountKey, number>>

export interface ListReviewQueueResponse {
  data: ReviewQueueItem[]
  /**
   * Rows matching the request INCLUDING its `filter` — so this governs
   * pagination, and only equals the sum of {@link counts} when `filter` is
   * 'all'. Swapping the two breaks the page count.
   */
  total: number
  page: number
  limit: number
  /**
   * Per-chip counts. Optional on this side of the wire even though the EPS
   * schema makes the map itself required: the client also talks to backends
   * that predate it, and an absent map has to read as "counts unavailable"
   * rather than as eight zeroes.
   */
  counts?: ReviewQueueCounts
}

// ── wire → view-model mappers ────────────────────────────────────────────────
// Each `Wire*` type mirrors a response interface above but with its money
// field(s) replaced by integer-cents `*Cents`; the paired mapper strips the
// cents field(s) and emits the dollar-shaped view model the components consume.

type WireMonthlyPayment = Omit<MonthlyPayment, 'amount'> & {
  amountCents: number
}
export const mapMonthlyPayment = ({
  amountCents,
  ...rest
}: WireMonthlyPayment): MonthlyPayment => ({
  ...rest,
  // String-typed dollar amount. `toFixed(2)` reproduces the legacy 2-decimal
  // wire string so any downstream dedupe key / Number() read stays identical.
  amount: centsToDollars(amountCents).toFixed(2),
})

type WireMonthlyTotal = Omit<
  MonthlyTotal,
  'totalPaid' | 'expected' | 'credited' | 'netDue' | 'payments'
> & {
  totalPaidCents: number
  expectedCents: number | null
  creditedCents: number
  netDueCents: number | null
  payments: WireMonthlyPayment[]
}
export const mapMonthlyTotal = ({
  totalPaidCents,
  expectedCents,
  creditedCents,
  netDueCents,
  payments,
  ...rest
}: WireMonthlyTotal): MonthlyTotal => ({
  ...rest,
  totalPaid: centsToDollars(totalPaidCents),
  expected: expectedCents == null ? null : centsToDollars(expectedCents),
  credited: centsToDollars(creditedCents),
  netDue: netDueCents == null ? null : centsToDollars(netDueCents),
  payments: payments.map(mapMonthlyPayment),
})

type WireBenefitRow = Omit<
  BenefitRow,
  'carrierExpected' | 'surecoExpected' | 'monthlyTotals'
> & {
  carrierExpectedCents: number | null
  surecoExpectedCents: number | null
  monthlyTotals: Record<string, WireMonthlyTotal>
}
export const mapBenefitRow = ({
  carrierExpectedCents,
  surecoExpectedCents,
  monthlyTotals,
  ...rest
}: WireBenefitRow): BenefitRow => ({
  ...rest,
  carrierExpected:
    carrierExpectedCents == null ? null : centsToDollars(carrierExpectedCents),
  surecoExpected:
    surecoExpectedCents == null ? null : centsToDollars(surecoExpectedCents),
  monthlyTotals: Object.fromEntries(
    Object.entries(monthlyTotals).map(([k, v]) => [k, mapMonthlyTotal(v)])
  ),
})

type WireListBenefitsResponse = Omit<ListBenefitsResponse, 'data'> & {
  data: WireBenefitRow[]
}

type WirePeriodSuggestion = Omit<
  PeriodSuggestion,
  'expectedAmount' | 'currentTotal'
> & {
  expectedAmountCents: number | null
  currentTotalCents: number
}
export const mapPeriodSuggestion = ({
  expectedAmountCents,
  currentTotalCents,
  ...rest
}: WirePeriodSuggestion): PeriodSuggestion => ({
  ...rest,
  expectedAmount:
    expectedAmountCents == null ? null : centsToDollars(expectedAmountCents),
  currentTotal: centsToDollars(currentTotalCents),
})

type WireMotherTransactionSlice = Omit<MotherTransactionSlice, 'amount'> & {
  amountCents: number | string
}
export const mapMotherTransactionSlice = ({
  amountCents,
  ...rest
}: WireMotherTransactionSlice): MotherTransactionSlice => ({
  ...rest,
  // `Number()` tolerates a bigint-serialized string; `toFixed(2)` keeps the
  // signed 2-decimal string shape the Adjust modal's split/dedupe logic expects.
  amount: (Number(amountCents) / 100).toFixed(2),
})

type WirePeriodDetailTransaction = Omit<
  PeriodDetailTransaction,
  'transactionNumber' | 'fullAmount' | 'amountInThisPeriod' | 'slices'
> & {
  // Optional until every EPS deploy sends it.
  transactionNumber?: string | null
  fullAmountCents: number
  amountInThisPeriodCents: number
  slices: WireMotherTransactionSlice[]
}
export const mapPeriodDetailTransaction = ({
  transactionNumber,
  fullAmountCents,
  amountInThisPeriodCents,
  slices,
  ...rest
}: WirePeriodDetailTransaction): PeriodDetailTransaction => ({
  ...rest,
  transactionNumber: transactionNumber ?? null,
  fullAmount: centsToDollars(fullAmountCents),
  amountInThisPeriod: centsToDollars(amountInThisPeriodCents),
  slices: slices.map(mapMotherTransactionSlice),
})

type WirePeriodDetailCredit = Omit<PeriodDetailCredit, 'amount'> & {
  amountCents: number
}
export const mapPeriodDetailCredit = ({
  amountCents,
  ...rest
}: WirePeriodDetailCredit): PeriodDetailCredit => ({
  ...rest,
  amount: centsToDollars(amountCents),
})

type WirePeriodDetail = Omit<
  PeriodDetail,
  'totalPaid' | 'expectedAmount' | 'credits' | 'netDue' | 'transactions'
> & {
  totalPaidCents: number
  expectedAmountCents: number | null
  credits: WirePeriodDetailCredit[]
  netDueCents: number | null
  transactions: WirePeriodDetailTransaction[]
}
export const mapPeriodDetail = ({
  totalPaidCents,
  expectedAmountCents,
  credits,
  netDueCents,
  transactions,
  ...rest
}: WirePeriodDetail): PeriodDetail => ({
  ...rest,
  totalPaid: centsToDollars(totalPaidCents),
  expectedAmount:
    expectedAmountCents == null ? null : centsToDollars(expectedAmountCents),
  credits: credits.map(mapPeriodDetailCredit),
  netDue: netDueCents == null ? null : centsToDollars(netDueCents),
  transactions: transactions.map(mapPeriodDetailTransaction),
})

type WireReviewTransaction = Omit<ReviewTransaction, 'amount'> & {
  amountCents: number
}
export const mapReviewTransaction = ({
  amountCents,
  ...rest
}: WireReviewTransaction): ReviewTransaction => ({
  ...rest,
  amount: centsToDollars(amountCents),
})

type WireReconIssueRow = Omit<
  ReconIssueRow,
  'expected' | 'paid' | 'discrepancy'
> & {
  expectedCents: number | null
  paidCents: number
  discrepancyCents: number
}
export const mapReconIssueRow = ({
  expectedCents,
  paidCents,
  discrepancyCents,
  ...rest
}: WireReconIssueRow): ReconIssueRow => ({
  ...rest,
  expected: expectedCents == null ? null : centsToDollars(expectedCents),
  paid: centsToDollars(paidCents),
  discrepancy: centsToDollars(discrepancyCents),
})

type WireReviewQueueItem = Omit<ReviewQueueItem, 'transaction' | 'issue'> & {
  transaction: WireReviewTransaction | null
  issue: WireReconIssueRow | null
}
export const mapReviewQueueItem = ({
  transaction,
  issue,
  ...rest
}: WireReviewQueueItem): ReviewQueueItem => ({
  ...rest,
  transaction: transaction == null ? null : mapReviewTransaction(transaction),
  issue: issue == null ? null : mapReconIssueRow(issue),
})

type WireListReviewQueueResponse = Omit<ListReviewQueueResponse, 'data'> & {
  data: WireReviewQueueItem[]
}

const BASE = '/reconciliation-v2'

/**
 * Every mutation invalidates every list/detail query so both tabs stay in sync.
 * `refetchType: 'all'` also refreshes inactive queries (drawer may be closed).
 */
const RECON_V2_QUERY_KEYS: readonly (readonly string[])[] = [
  ['recon-v2-benefits'],
  ['recon-v2-review-queue'],
  ['recon-v2-period'],
  ['recon-v2-suggest'],
  ['recon-v2-history'],
  ['recon-v2-benefit-search'],
  ['recon-v2-tx-benefit'],
  // Transaction Details tab surfaces (own query-key namespace) — so an
  // allocate/absorb/deallocate from Benefits or Review keeps the ledger rows,
  // the "Absorbed" stat card, and a mounted event drawer in sync.
  ['transaction-details'],
  ['transaction-details-summary'],
  ['transaction-events'],
  // Reconciliation Reports page (monthly + annual summary).
  ['recon-v2-monthly-summary'],
  ['recon-v2-annual-summary'],
] as const

const invalidateAllReconV2 = (qc: ReturnType<typeof useQueryClient>) => {
  for (const key of RECON_V2_QUERY_KEYS) {
    qc.invalidateQueries({
      queryKey: key as readonly unknown[],
      refetchType: 'all',
    })
  }
}

export const useListBenefits = (
  params: ListBenefitsParams,
  options?: Partial<UseQueryOptions<ListBenefitsResponse>>
) =>
  useQuery<ListBenefitsResponse>({
    queryKey: ['recon-v2-benefits', params],
    queryFn: async () => {
      const res = await apiClient.get<WireListBenefitsResponse>(
        `${BASE}/benefits`,
        { params }
      )
      return { ...res.data, data: res.data.data.map(mapBenefitRow) }
    },
    placeholderData: keepPreviousData,
    // Serve cache on tab switches / remounts; mutations still invalidate
    // (invalidateAllReconV2) so data refreshes after any write.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  })

export type BenefitDefaultExpectedSource = 'carrier' | 'calculated' | 'bundle'

export interface BenefitSearchResult {
  benefitId: string
  employeeName: string
  employeePublicKey: string
  insuredPersons: BenefitInsuredPerson[]
  carrierName: string | null
  benefitEffectiveDate: string | null
  benefitEndDate: string | null
  companyName: string | null
  /**
   * Benefit-level default expected, in DOLLARS (confirmed carrier cost →
   * calculated plan cost → bundle). Not month-aware: per-period overrides are
   * not applied. Null when no source has a non-zero value.
   */
  defaultExpected: number | null
  defaultExpectedSource: BenefitDefaultExpectedSource | null
}

/**
 * Wire shape of GET /benefit-search: the expected amount is INTEGER CENTS. The
 * expected fields are optional so the picker tolerates a backend that predates
 * them.
 */
interface WireBenefitSearchResult
  extends Omit<
    BenefitSearchResult,
    'defaultExpected' | 'defaultExpectedSource'
  > {
  defaultExpectedCents?: number | null
  defaultExpectedSource?: BenefitDefaultExpectedSource | null
}

/** Wire → view model (cents → dollars). Exported for unit tests. */
export const mapBenefitSearchResult = ({
  defaultExpectedCents,
  defaultExpectedSource,
  ...rest
}: WireBenefitSearchResult): BenefitSearchResult => ({
  ...rest,
  defaultExpected:
    defaultExpectedCents == null ? null : centsToDollars(defaultExpectedCents),
  defaultExpectedSource: defaultExpectedSource ?? null,
})

export const useBenefitSearch = (
  params: {
    search?: string
    /**
     * Allocation year (scopeYear). Anchors a ±1-year search window on the
     * backend (scopeYear-1 … scopeYear+1) so adjacent-year benefits are
     * selectable; exact-scopeYear rows are still ranked first.
     */
    year?: number
    /**
     * Benefit id whose employee the results must share. Lets the picker list
     * that employee's benefits before the operator types anything.
     */
    sameEmployeeAs?: string
    limit?: number
  },
  options?: Partial<UseQueryOptions<BenefitSearchResult[]>>
) =>
  useQuery<BenefitSearchResult[]>({
    queryKey: ['recon-v2-benefit-search', params],
    queryFn: async () => {
      const res = await apiClient.get<WireBenefitSearchResult[]>(
        `${BASE}/benefit-search`,
        { params }
      )
      return res.data.map(mapBenefitSearchResult)
    },
    // Re-typing or backspacing to an earlier term within a minute reuses the
    // cached page instead of re-querying. Mutations still invalidate this key.
    staleTime: 60 * 1000,
    ...options,
    // The guard is applied AFTER the spread so a caller can't override it and
    // fire GET /benefit-search with neither a term nor an employee anchor (year
    // alone used to list on open; that was removed — it scanned every benefit).
    enabled:
      (!!params.search?.trim() || !!params.sameEmployeeAs) &&
      (options?.enabled ?? true),
  })

export const useSuggestPeriods = (
  params: {
    benefitId: string | null | undefined
    amount: number | null
    transactionDate?: string
  },
  options?: Partial<UseQueryOptions<PeriodSuggestion[]>>
) =>
  useQuery<PeriodSuggestion[]>({
    queryKey: ['recon-v2-suggest', params],
    queryFn: async () => {
      const res = await apiClient.get<WirePeriodSuggestion[]>(
        `${BASE}/suggestions/${params.benefitId}`,
        {
          params: {
            // Query param is now integer cents. Guard the null case (the query
            // is `enabled` only when amount != null, but keep it defensive).
            amountCents:
              params.amount == null ? undefined : dollarsToCents(params.amount),
            transactionDate: params.transactionDate,
          },
        }
      )
      return res.data.map(mapPeriodSuggestion)
    },
    ...options,
    // The guard is applied AFTER the spread so a caller passing `enabled: true`
    // cannot override it and fire GET /suggestions with a missing benefitId/amount.
    enabled:
      !!params.benefitId && params.amount != null && (options?.enabled ?? true),
  })

export const useGetPeriodDetail = (
  benefitId: string | undefined,
  periodStartDate: string | undefined,
  options?: Partial<UseQueryOptions<PeriodDetail>>
) =>
  useQuery<PeriodDetail>({
    queryKey: ['recon-v2-period', benefitId, periodStartDate],
    enabled: !!benefitId && !!periodStartDate,
    queryFn: async () => {
      const res = await apiClient.get<WirePeriodDetail>(
        `${BASE}/period/${benefitId}/${periodStartDate}`
      )
      return mapPeriodDetail(res.data)
    },
    ...options,
  })

export interface ResolvedTransactionBenefit {
  benefitId: string
  employeeName: string
  insuredPersons: BenefitInsuredPerson[]
  carrierName: string | null
}

/**
 * Date-aware benefit for a card transaction — the authoritative pick for the
 * Adjust drawer's refund prefill. The review list hint is card-latest (not
 * date-aware); this resolves the benefit whose window covers the transaction,
 * matching what auto-allocate would write. Returns null if unresolved.
 */
export const useResolveTransactionBenefit = (
  txId: string | null | undefined,
  options?: Partial<UseQueryOptions<ResolvedTransactionBenefit | null>>
) =>
  useQuery<ResolvedTransactionBenefit | null>({
    queryKey: ['recon-v2-tx-benefit', txId],
    enabled: !!txId,
    queryFn: async () => {
      const res = await apiClient.get<ResolvedTransactionBenefit | null>(
        `${BASE}/transaction-benefit/${txId}`
      )
      return res.data
    },
    ...options,
  })

export interface ReviewQueueParams {
  search?: string
  filter?: ReviewQueueFilter
  /**
   * Which arm to page. REQUIRED, and EPS 400s without it: the two arms are
   * separate queries with separate pagination, so there is no combined feed to
   * fall back to. Both call sites already pass one — leaving this optional
   * would only let a future caller fail at runtime instead of compile time.
   */
  kind: ReviewQueueKind
  page?: number
  limit?: number
}

/**
 * One arm of the Required-action queue: a date-sorted, server-paginated feed of
 * either the transactions the auto-allocator could not place or the open
 * overpaid/underpaid/manual-review discrepancy issues. `kind` picks the arm —
 * which is what the two Required-action tabs each do — and `total` governs that
 * arm's pagination alone. There is no combined feed: the arms are separate
 * queries server-side, and merging two independently-paged sets would repeat
 * and drop rows across the page boundary.
 *
 * Query key is prefixed 'recon-v2-review-queue' so `invalidateAllReconV2`
 * refreshes it after any mutation.
 */
export const useReviewQueue = (
  params: ReviewQueueParams,
  options?: Partial<UseQueryOptions<ListReviewQueueResponse>>
) =>
  useQuery<ListReviewQueueResponse>({
    queryKey: ['recon-v2-review-queue', params],
    queryFn: async () => {
      const res = await apiClient.get<WireListReviewQueueResponse>(
        `${BASE}/review-queue`,
        { params }
      )
      return { ...res.data, data: res.data.data.map(mapReviewQueueItem) }
    },
    placeholderData: keepPreviousData,
    // Serve cache on tab switches; mutations invalidate to refresh.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  })

/**
 * The params each queue tab mounts with. Both tab labels need per-`kind` totals
 * while the open tab is fetching its own page, so every count call site goes
 * through {@link useReviewQueueCount} and spreads THIS object — that way all of
 * them hash to the same React Query key as the tab sitting at its defaults, and
 * they collapse into one request per `kind`.
 *
 * A tab that has been searched/filtered/paged away from these defaults
 * diverges deliberately: its own query pays for its own page, and the counts
 * stay the unfiltered grand totals they are labelled as.
 */
export const REVIEW_QUEUE_DEFAULTS = {
  filter: 'all',
  page: 1,
  limit: 25,
  // Everything BUT `kind` — each call site supplies its own arm, and the point
  // of this object is that the rest match exactly so the count query and the
  // open tab's first page hash to one key.
} as const satisfies Omit<ReviewQueueParams, 'kind'>

/**
 * Unfiltered row count for one arm of the Required-action queue — the number in
 * that arm's tab label. Never summed across the arms: the "Required action" tab
 * that carried a grand total is gone, and each label now speaks only for itself.
 * Deliberately NOT scoped to the caller's search/filter/page.
 */
export const useReviewQueueCount = (kind: ReviewQueueKind): number => {
  const { data } = useReviewQueue({ kind, ...REVIEW_QUEUE_DEFAULTS })
  return data?.total ?? 0
}

export interface AllocateBatchItem {
  benefitId: string
  periodStartDate: string
  amount: string
  classification?: AllocationClassification
  // Per-item: the backend appends one NOTE_ADDED event per item carrying a note.
  // (Reviewer asked to hoist this to request-level — deferred; needs a matching
  // backend AllocateBatchRequestDto + service change to stay consistent.)
  note?: string
}

export interface AllocateBatchRequest {
  transactionId: string
  items: AllocateBatchItem[]
  // Prior allocations matching the scope are removed in the same DB tx
  // before items are applied — the move-and-split atomicity contract.
  removePriorScope?: {
    benefitId?: string
    periodStartDate?: string
  }
  // When true, EVERY live allocation of the transaction is removed before the
  // batch is applied — the Adjust flow re-splits the whole tx from its mother
  // amount so re-adjusting always starts from the original transaction.
  replaceEntireTransaction?: boolean
  // Allocate-with-remainder: absorb whatever the operator left unallocated as a
  // company write-off, atomically with the batch. Never an item (it must not
  // become a period_allocation row).
  absorption?: {
    /** Positive dollar magnitude of the absorbed remainder (e.g. "50.00"). */
    amount: string
    absorptionType: AbsorptionType
    /**
     * The operator's justification for the write-off, trimmed, max 500.
     * MUST be sent on every absorb submit: `allocateBatch` hard-deletes every
     * absorption on the transaction and re-records it from this body, so a note
     * omitted here silently becomes NULL. There is no carry-forward server-side
     * (enrollment-platform ADR 0003).
     */
    note?: string
  }
}

export const useAllocateBatch = (
  options?: UseMutationOptions<unknown, unknown, AllocateBatchRequest>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (req: AllocateBatchRequest) => {
      // Convert each item's SIGNED dollar amount to signed integer cents.
      // `Number('-125.00')` preserves the refund sign; `dollarsToCents` rounds.
      const wire = {
        ...req,
        items: req.items.map(({ amount, ...rest }) => ({
          ...rest,
          amountCents: dollarsToCents(Number(amount)),
        })),
        absorption: req.absorption
          ? {
              amountCents: dollarsToCents(Number(req.absorption.amount)),
              absorptionType: req.absorption.absorptionType,
              // Explicit — this object is rebuilt field by field, so a note left
              // off here is dropped on the floor rather than type-erroring.
              note: req.absorption.note,
            }
          : undefined,
      }
      const res = await apiClient.post(`${BASE}/allocate-batch`, wire)
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

export interface RecordAbsorptionRequest {
  transactionId: string
  /** Positive dollar magnitude to absorb (e.g. "50.00"). */
  amount: string
  absorptionType: AbsorptionType
  /** The operator's justification for the write-off, trimmed, max 500. */
  note?: string
}

/**
 * Records a company-absorbed write-off of a portion of a transaction we overpaid
 * or double-paid. The absorbed amount is kept out of period_allocation.
 */
export const useRecordAbsorption = (
  options?: UseMutationOptions<unknown, unknown, RecordAbsorptionRequest>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ amount, ...rest }: RecordAbsorptionRequest) => {
      const res = await apiClient.post(`${BASE}/absorption`, {
        ...rest,
        amountCents: dollarsToCents(Number(amount)),
      })
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

export interface EePaidRequest {
  benefitId: string
  /** First day of the month being paid (YYYY-MM-DD). */
  periodStartDate: string
  /** Positive dollar amount, two decimals (e.g. "125.00"). */
  amount: string
  /** ISO timestamp from the manual date+time inputs. */
  transactionDate?: string
  note?: string
}

/**
 * Records a manual EE-Paid transaction (operator-entered payment made outside
 * the card rails). Backs the "Add Payment" manual mode of the Adjust drawer.
 */
export const useEePaid = (
  options?: UseMutationOptions<unknown, unknown, EePaidRequest>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ amount, ...rest }: EePaidRequest) => {
      // Positive dollar amount → positive integer cents.
      const res = await apiClient.post(`${BASE}/ee-paid`, {
        ...rest,
        amountCents: dollarsToCents(Number(amount)),
      })
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

export interface ManualRefundRequest {
  benefitId: string
  /** First day of the month being refunded (YYYY-MM-DD). */
  periodStartDate: string
  /** Positive dollar magnitude, two decimals — stored as a negative refund. */
  amount: string
  /** Rail the refund came back on — operator-selected, mandatory. */
  source: ManualRefundSource
  /** Digits-only check number — required when `source` is Check, never sent for Group ACH. */
  checkNumber?: string
  /** Refund reason classification (REFUND_*); defaults to REFUND_OTHER server-side. */
  reason?: AllocationClassification
  /** ISO timestamp from the manual date+time inputs. */
  transactionDate?: string
  note?: string
}

/**
 * Records a manual refund on a check / group_ach rail. Backs the Refund option
 * of the Adjust drawer's manual "Add Payment" mode.
 */
export const useRecordManualRefund = (
  options?: UseMutationOptions<unknown, unknown, ManualRefundRequest>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ amount, ...rest }: ManualRefundRequest) => {
      // Positive dollar magnitude → positive integer cents (stored negative
      // server-side).
      const res = await apiClient.post(`${BASE}/manual-refund`, {
        ...rest,
        amountCents: dollarsToCents(Number(amount)),
      })
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

export interface DeallocateRequest {
  transactionId: string
  /** Scope the removal to one benefit (omit to clear every live slice). */
  benefitId?: string
  /** Scope the removal to one period (YYYY-MM-DD, first of month). */
  periodStartDate?: string
  reason?: string
  /**
   * Permanent manual hold — the auto-allocate sweep never reclaims the
   * transaction until it is re-assigned. Used by the "Remove allocation" flow
   * for rail-sourced charges (which the sweep would otherwise re-allocate).
   */
  hold?: boolean
}

/**
 * Removes a transaction's live allocation(s). Backs the "Delete transaction"
 * action on manually-added (EE-Paid) transactions in the Adjust modal.
 */
export const useDeallocate = (
  options?: UseMutationOptions<unknown, unknown, DeallocateRequest>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (req: DeallocateRequest) => {
      const res = await apiClient.post(`${BASE}/deallocate`, req)
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

/**
 * Permanently deletes a manually-added payment (EE Paid / Check / Group ACH).
 * Backend rejects rail-sourced txns and payments that still have a live
 * allocation. Irreversible — no soft-delete.
 */
export const useDeleteManualPayment = (
  options?: UseMutationOptions<unknown, unknown, { transactionId: string }>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (req: { transactionId: string }) => {
      const res = await apiClient.post(`${BASE}/delete-payment`, req)
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

export interface ReturnTransactionRequest {
  transactionId: string
  /** Required, trimmed, ≤ 500 chars. */
  note: string
}

/**
 * Returns an unallocated, un-absorbed iStream transaction: it leaves the review queue,
 * the auto-allocate sweep and the Pending count, and lists as "Returned" in the
 * Transactions ledger. Backend rejects allocated / absorbed / already-returned
 * txns. Reversible via {@link useUnreturnTransaction}.
 */
export const useReturnTransaction = (
  options?: UseMutationOptions<unknown, unknown, ReturnTransactionRequest>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (req: ReturnTransactionRequest) => {
      const res = await apiClient.post(`${BASE}/return-transaction`, req)
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

/** Reverses a return; the tx rejoins the review queue and auto-allocate sweep. */
export const useUnreturnTransaction = (
  options?: UseMutationOptions<unknown, unknown, { transactionId: string }>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (req: { transactionId: string }) => {
      const res = await apiClient.post(`${BASE}/unreturn-transaction`, req)
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

export interface ResolveIssueRequest {
  benefitId: string
  periodStartDate: string
  note?: string
}

export const useResolveIssue = (
  options?: UseMutationOptions<unknown, unknown, ResolveIssueRequest>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (req: ResolveIssueRequest) => {
      const res = await apiClient.post(`${BASE}/resolve-issue`, req)
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

export interface LinkZendeskTicketRequest {
  benefitId: string
  periodStartDate: string
  ticketUrl: string
  note?: string
}

export interface LinkZendeskTicketResponse {
  ticketUrl: string
  ticketId: number | null
}

export const useLinkZendeskTicket = (
  options?: UseMutationOptions<
    LinkZendeskTicketResponse,
    unknown,
    LinkZendeskTicketRequest
  >
) => {
  const qc = useQueryClient()
  return useMutation<
    LinkZendeskTicketResponse,
    unknown,
    LinkZendeskTicketRequest
  >({
    mutationFn: async (req) => {
      const res = await apiClient.post<LinkZendeskTicketResponse>(
        `${BASE}/link-zendesk-ticket`,
        req
      )
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      qc.invalidateQueries({ queryKey: ['recon-v2-tickets'] })
      options?.onSuccess?.(...args)
    },
  })
}

export interface BenefitTicket {
  id: string
  periodStartDate: string
  ticketUrl: string
  note: string | null
  createdAt: string
}

/** Still-linked Zendesk tickets for a benefit — drives the Support-tickets drawer. */
export const useBenefitTickets = (
  benefitId: string | null,
  options?: Partial<UseQueryOptions<BenefitTicket[]>>
) =>
  useQuery<BenefitTicket[]>({
    queryKey: ['recon-v2-tickets', benefitId],
    enabled: !!benefitId,
    queryFn: async () => {
      const res = await apiClient.get<BenefitTicket[]>(
        `${BASE}/tickets/${benefitId}`
      )
      return res.data
    },
    ...options,
  })

/** One row of `GET /reconciliation-v2/history` — the recon event stream. */
export interface HistoryEvent {
  id: string
  createdAt: string
  eventType: string
  benefitId: string | null
  /** First day (YYYY-MM-DD) of the benefit-month the event is about. */
  periodStartDate: string | null
  payload: Record<string, unknown>
  createdBy: string | null
  employeeName: string | null
  /** Populated when the backend resolves the benefit's employee (null otherwise). */
  employeePublicKey?: string | null
  /** Insured persons for the benefit-row, parsed from `persons` JSON. */
  insuredPersons?: BenefitInsuredPerson[]
  /** Carrier name for the benefit-row, when available. */
  carrierName?: string | null
  /** Company name for the client associated with the benefit, when available. */
  companyName?: string | null
  /** Source transaction id extracted from the event payload, when present. */
  transactionId?: string | null
  amount: number | string | null
}

export interface HistoryResponse {
  data: HistoryEvent[]
  total: number
  page: number
  limit: number
}

type WireHistoryEvent = Omit<HistoryEvent, 'amount'> & {
  amountCents: number | null
}
export const mapHistoryEvent = ({
  amountCents,
  ...rest
}: WireHistoryEvent): HistoryEvent => ({
  ...rest,
  amount: amountCents == null ? null : centsToDollars(amountCents),
})

type WireHistoryResponse = Omit<HistoryResponse, 'data'> & {
  data: WireHistoryEvent[]
}

/**
 * Activity feed for one benefit — drives the Activity-log drawer.
 *
 * Uses the dedicated `?benefitId` filter on `/history` (exact-match, backed by
 * `idx_period_event_benefit_recent`). Consumers still filter the returned
 * events to `event.benefitId === benefitId` client-side as a belt-and-suspenders.
 */
export const useBenefitActivity = (
  benefitId: string | null,
  options?: Partial<UseQueryOptions<HistoryResponse>>
) =>
  useQuery<HistoryResponse>({
    // Prefixed 'recon-v2-history' so invalidateAllReconV2 refreshes it too.
    queryKey: ['recon-v2-history', 'benefit-activity', benefitId],
    enabled: !!benefitId,
    queryFn: async () => {
      const res = await apiClient.get<WireHistoryResponse>(`${BASE}/history`, {
        params: { benefitId, limit: 200 },
      })
      return { ...res.data, data: res.data.data.map(mapHistoryEvent) }
    },
    ...options,
  })

export interface UnlinkZendeskTicketRequest {
  benefitId: string
  periodStartDate: string
  ticketUrl: string
}

export const useUnlinkZendeskTicket = (
  options?: UseMutationOptions<unknown, unknown, UnlinkZendeskTicketRequest>
) => {
  const qc = useQueryClient()
  return useMutation<unknown, unknown, UnlinkZendeskTicketRequest>({
    mutationFn: async (req) => {
      const res = await apiClient.post(`${BASE}/unlink-zendesk-ticket`, req)
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      qc.invalidateQueries({ queryKey: ['recon-v2-tickets'] })
      options?.onSuccess?.(...args)
    },
  })
}

export interface SetExpectedPriceRequest {
  benefitId: string
  /** First day of the month (YYYY-MM-DD). */
  periodStartDate: string
  /** Decimal string, e.g. "123.45". Must satisfy /^-?\d+(\.\d{1,2})?$/. */
  expected: string
  note?: string
}

/**
 * Appends an EXPECTED_PRICE_SET event for a benefit-period. The new event
 * becomes the top-precedence expected in every subsequent read.
 *
 * On success, invalidates every recon-v2 query family so the grid, detail
 * panel, and history drawer all refresh automatically.
 */
export const useSetExpectedPrice = (
  options?: UseMutationOptions<
    PeriodEventView,
    unknown,
    SetExpectedPriceRequest
  >
) => {
  const qc = useQueryClient()
  return useMutation<PeriodEventView, unknown, SetExpectedPriceRequest>({
    mutationFn: async ({ expected, ...rest }) => {
      // `expected` is the normalized dollar string from the helper; convert to
      // integer cents on the wire.
      const res = await apiClient.post<PeriodEventView>(
        `${BASE}/expected-price`,
        { ...rest, expectedCents: dollarsToCents(Number(expected)) }
      )
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

export interface SetPullDateRequest {
  benefitId: string
  /** Day of month the carrier pulls the premium, 1–31. */
  pullDay: number
}

export interface PullDateResponse {
  benefitId: string
  pullDay: number
  source: 'BACKFILL' | 'MAKER' | 'FINANCE'
  updatedBy: string | null
}

/**
 * Finance sets a benefit's autopay pull day. Overwrites the maker, backfill or
 * earlier finance value and takes effect immediately — there is no "apply next
 * month". On success, invalidates every recon-v2 query family so the Benefits
 * list (pull date, missed flag) and an open period detail refresh.
 */
export const useSetPullDate = (
  options?: UseMutationOptions<PullDateResponse, unknown, SetPullDateRequest>
) => {
  const qc = useQueryClient()
  return useMutation<PullDateResponse, unknown, SetPullDateRequest>({
    mutationFn: async (req) => {
      const res = await apiClient.post<PullDateResponse>(
        `${BASE}/pull-date`,
        req
      )
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

export interface RecordExplicitCreditRequest {
  benefitId: string
  /** First day of the credited month (YYYY-MM-DD). */
  periodStartDate: string
  /** Positive dollar magnitude, two decimals (e.g. "100.00"). */
  amount: string
  reason: ExplicitCreditReason
  /**
   * Carrier/billing context only — a rebate reference, a carrier correspondence
   * id. Never member names, dates of birth, or any other identifier.
   */
  note?: string
}

/**
 * Records an explicit credit on a benefit-period: the carrier charged less than
 * the expected price, so the member is owed that much back on a later invoice.
 * The expected price itself is left untouched — issues are then derived against
 * net due (expected less credits).
 *
 * Rejected by the server when the period has no live allocation or when the
 * credits would exceed what the period is expected to cost.
 */
export const useRecordExplicitCredit = (
  options?: UseMutationOptions<unknown, unknown, RecordExplicitCreditRequest>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ amount, ...rest }: RecordExplicitCreditRequest) => {
      // Positive dollar magnitude → positive integer cents.
      const res = await apiClient.post(`${BASE}/explicit-credit`, {
        ...rest,
        amountCents: dollarsToCents(Number(amount)),
      })
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

/**
 * Hard-deletes one explicit credit. The server answers 409 once the invoicing
 * side has consumed it — a consumed credit is corrected with a new compensating
 * record, never by mutation.
 */
export const useDeleteExplicitCredit = (
  options?: UseMutationOptions<unknown, unknown, { creditId: string }>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (req: { creditId: string }) => {
      const res = await apiClient.post(`${BASE}/delete-explicit-credit`, req)
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

export interface FlagIssueRequest {
  benefitId: string
  /** First day of the affected month (YYYY-MM-DD). */
  periodStartDate: string
  note?: string
}

/**
 * Persists a manual-review flag for a benefit-period. The flag survives every
 * subsequent allocate/deallocate recompute and appears in the issues list as
 * `issueType: 'manual_review'`. Cleared via `useUnflagIssue`.
 */
export const useFlagIssue = (
  options?: UseMutationOptions<unknown, unknown, FlagIssueRequest>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (req: FlagIssueRequest) => {
      const res = await apiClient.post(`${BASE}/flag-issue`, req)
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}

/**
 * Clears a manual-review flag for a benefit-period (persisted unflag).
 * Does not affect auto-detected overpaid/underpaid issues.
 */
export const useUnflagIssue = (
  options?: UseMutationOptions<unknown, unknown, FlagIssueRequest>
) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (req: FlagIssueRequest) => {
      const res = await apiClient.post(`${BASE}/unflag-issue`, req)
      return res.data
    },
    ...options,
    onSuccess: (...args) => {
      invalidateAllReconV2(qc)
      options?.onSuccess?.(...args)
    },
  })
}
